import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode, SyntheticEvent } from "react";
import type { SxProps, Theme } from "@mui/material";
import { Tab, Typography } from "@mui/material";
import categories from "constants/Categories";
import { TabList, TabContext } from "@mui/lab";
import { useCatalogue } from "providers";
import CataloguePageSkeleton from "./CataloguePageSkeleton";
import CategorieCataloguePanel from "./CategorieCataloguePanel";

const sxTab: SxProps<Theme> = (theme: Theme) => ({
  color: theme.palette.grey.A700,
  backgroundColor: "transparent",
  position: "relative",
  cursor: "pointer",
  "& .MuiSvgIcon-root": {
    fontSize: 35,
    marginBottom: "20px",
  },
  "&:hover": {
    color: theme.palette.primary.main,
  },
  ".label-categorie": { position: "absolute", bottom: "3px" },
});

function getGridRootParent(parent: HTMLElement | null): HTMLElement | null {
  if (parent == null) {
    return null;
  }
  if (parent?.classList.contains("MuiContainer-root") ?? false) {
    return parent;
  } else {
    return getGridRootParent(parent?.parentElement);
  }
}

function calculatePixelsToScroll(el: Element, headerHeight: number): number {
  let parent = el.parentElement;
  parent = getGridRootParent(parent);

  if (parent != null) {
    const paddingTop = globalThis.getComputedStyle(parent, null).getPropertyValue("padding-top");
    let n: number = 16;
    try {
      n = Number.parseInt(/\d*/i.exec(paddingTop)?.at(0) ?? "16");
    } catch (error) {}
    const pixelsToScroll = parent.offsetTop + 2 + n - headerHeight;
    return pixelsToScroll;
  } else {
    return 0;
  }
}

function CategorieTabs(): ReactNode {
  const { isArticlesLoading } = useCatalogue();

  const [value, setValue] = useState<string>(categories[0].dataBaseLabel);
  const [headerHeight, setHeaderHeight] = useState<number>(0);

  function calculateHeaderHeight(): void {
    const height = document.querySelector(".real-header")?.getBoundingClientRect()?.height ?? 64;
    setHeaderHeight(height);
  }

  useEffect(() => {
    window.addEventListener("resize", calculateHeaderHeight);
    calculateHeaderHeight();
    return () => {
      window.removeEventListener("resize", calculateHeaderHeight);
    };
  }, []);

  const sxTabsList: SxProps = useMemo(
    () => ({
      position: "sticky",
      top: `${headerHeight}px`,
      zIndex: "1099",
      backgroundColor: "white",
      ".MuiTabs-flexContainer": {
        md: { justifyContent: "center" },
      },
    }),
    [headerHeight]
  );

  const scrollToTop = useCallback(
    (event?: SyntheticEvent): void => {
      let pixelsToScroll = 86;
      if (event?.target instanceof Element) {
        const el = event.target;
        pixelsToScroll = calculatePixelsToScroll(el, headerHeight);
      }
      if (window.scrollY > pixelsToScroll) {
        window.scrollTo(0, pixelsToScroll);
      }
    },
    [headerHeight]
  );

  function handleChange(_event: SyntheticEvent, newValue: string): void {
    setValue(newValue);
  }

  if (isArticlesLoading) {
    return <CataloguePageSkeleton />;
  }

  return (
    <TabContext value={value}>
      <TabList
        onChange={handleChange}
        variant="scrollable"
        scrollButtons
        allowScrollButtonsMobile
        sx={sxTabsList}>
        {categories.map(({ dataBaseLabel, icon: Icon, printLabel }) => (
          <Tab
            value={dataBaseLabel}
            sx={sxTab}
            key={dataBaseLabel}
            icon={<Icon />}
            label={<Typography className="label-categorie">{printLabel}</Typography>}
            onClick={scrollToTop}
          />
        ))}
      </TabList>
      {categories.map(({ dataBaseLabel }) => (
        <CategorieCataloguePanel key={dataBaseLabel} categorieDatabaseLabel={dataBaseLabel} />
      ))}
    </TabContext>
  );
}

export default CategorieTabs;
